/**
 * CloudSaver — cloud article management (the largest component).
 * Progressive cloud loading, business categories, mass upload, article CRUD.
 */
import { useState, useMemo, useEffect, useCallback, useRef, type FC } from 'react';
import { Icon } from './Icon';
import * as ds from '../services/dataService';
import { apiPostForm } from '../services/api';
import { useAppStore } from '../stores/appStore';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';

/* ─── types ─── */
interface CloudFile {
  type: string;
  article: string;
  category: string;
  name: string;
  url: string;
  thumb?: string;
  mtime: number;
  article_created_mtime?: number;
  article_thumb?: string;
  print_name?: string;
  source_name?: string;
  source_url?: string;
  product_name?: string;
  render_prefix?: string;
  category_id?: string;
  category_name?: string;
  short_url?: string;
}
interface SourceFile {
  type: string;
  name: string;
  url?: string;
  thumb?: string;
}
interface BusinessCategory {
  id: string;
  name: string;
  keywords: string[];
  prefixes: string[];
  is_active: boolean;
  priority: number;
}
interface ArticleItem {
  key: string;
  article: string;
  thumbnail: string;
  mtime: number;
  print_name: string;
  source_name: string;
  source_url: string;
  categories: Record<string, CloudFile[]>;
}
interface MassUploadPreview {
  matched: { file: File; article: string }[];
  unmatched: { file: File; article: string | null; status: string; reason: string }[];
  allFiles: File[];
}
interface Props {
  files: SourceFile[];
}

/* ─── constants ─── */
const INITIAL_CATEGORY_ITEMS = 12;
const CATEGORY_ITEMS_STEP = 24;
const CLOUD_PER_PAGE = 50;

export const CloudSaver: FC<Props> = ({ files }) => {
  const password = useAppStore((s) => s.password);

  /* ─── state ─── */
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);
  const [isZipping, setIsZipping] = useState(false);
  const [previewFile, setPreviewFile] = useState<CloudFile | null>(null);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [busy, setBusy] = useState<{ type: string | null; key: string | null }>({
    type: null,
    key: null,
  });
  const [notification, setNotification] = useState<string | null>(null);
  const [editingArticleKey, setEditingArticleKey] = useState<string | null>(null);
  const [editingPrintNameValue, setEditingPrintNameValue] = useState('');
  const [useShortLinks, setUseShortLinks] = useState(() => {
    const s = localStorage.getItem('cloud_copy_short_links');
    return s === null ? true : s === 'true';
  });
  const [filter, setFilter] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [dragUploadTarget, setDragUploadTarget] = useState<string | null>(null);
  const [categoryVisibleCount, setCategoryVisibleCount] = useState<Record<string, number>>({});
  const [businessCategories, setBusinessCategories] = useState<BusinessCategory[]>([]);
  const [categoryStats, setCategoryStats] = useState<Record<string, number>>({});
  const [selectedBusinessCategory, setSelectedBusinessCategory] = useState('all');
  const [excludeMockups, setExcludeMockups] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryKeywords, setNewCategoryKeywords] = useState('');
  const [newCategoryPrefixes, setNewCategoryPrefixes] = useState('');
  const [isCategoryBusy, setIsCategoryBusy] = useState(false);
  const [openedFileCategoryMenuKey, setOpenedFileCategoryMenuKey] = useState<string | null>(null);
  const [massUploadCategory, setMassUploadCategory] = useState<'products' | 'mockups'>('products');
  const [isMassUploading, setIsMassUploading] = useState(false);
  const [massUploadResult, setMassUploadResult] = useState<any>(null);
  const [massUploadPreview, setMassUploadPreview] = useState<MassUploadPreview | null>(null);
  const [massUploadProgress, setMassUploadProgress] = useState<{
    done: number;
    total: number;
    currentFile: string;
  } | null>(null);
  const [massUploadAllowNew, setMassUploadAllowNew] = useState(false);

  /* progressive cloud loading */
  const [cloudFiles, setCloudFiles] = useState<CloudFile[]>([]);
  const [isCloudLoading, setIsCloudLoading] = useState(true);
  const [isCloudLoadingMore, setIsCloudLoadingMore] = useState(false);
  const [cloudPage, setCloudPage] = useState(0);
  const [hasMoreCloud, setHasMoreCloud] = useState(false);
  const [totalCloudArticles, setTotalCloudArticles] = useState(0);
  const cloudLoadedPages = useRef(0);

  /* ─── cloud loaders ─── */
  const loadCloudPage = useCallback(async (page = 1, append = false) => {
    page === 1 ? setIsCloudLoading(true) : setIsCloudLoadingMore(true);
    try {
      const data = await ds.loadCloudPage(page, CLOUD_PER_PAGE);
      if (append) setCloudFiles((prev) => [...prev, ...(data.files as unknown as CloudFile[])]);
      else setCloudFiles(data.files as unknown as CloudFile[]);
      setHasMoreCloud(data.hasMore);
      setTotalCloudArticles(data.total);
      setCloudPage(page);
      cloudLoadedPages.current = page;
    } finally {
      setIsCloudLoading(false);
      setIsCloudLoadingMore(false);
    }
  }, []);

  const loadMoreCloud = useCallback(() => {
    if (isCloudLoadingMore || !hasMoreCloud) return;
    loadCloudPage(cloudPage + 1, true);
  }, [cloudPage, hasMoreCloud, isCloudLoadingMore, loadCloudPage]);

  const reloadCloud = useCallback(async () => {
    const pages = Math.max(1, cloudLoadedPages.current);
    setIsCloudLoading(true);
    try {
      const data = await ds.loadCloudPage(1, pages * CLOUD_PER_PAGE);
      setCloudFiles(data.files as unknown as CloudFile[]);
      setHasMoreCloud(data.hasMore);
      setTotalCloudArticles(data.total);
      setCloudPage(Math.ceil((data.files.length || 1) / CLOUD_PER_PAGE));
    } finally {
      setIsCloudLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCloudPage(1);
  }, [loadCloudPage]);

  /* ─── business categories ─── */
  const loadBizCats = async () => {
    const data = await ds.loadCloudCategories();
    setBusinessCategories(data.categories || []);
    setCategoryStats((data.stats || {}) as Record<string, number>);
  };
  useEffect(() => {
    loadBizCats();
  }, []);

  const refresh = async () => {
    await reloadCloud();
  };

  const saveBizCats = async (next: BusinessCategory[]) => {
    setIsCategoryBusy(true);
    try {
      const saved = await ds.saveCloudCategories(next);
      if (!saved) {
        alert('Не удалось сохранить категории');
        return false;
      }
      setBusinessCategories(saved);
      await refresh();
      await loadBizCats();
      return true;
    } finally {
      setIsCategoryBusy(false);
    }
  };

  const handleAddBizCat = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    const parseTokens = (r: string) =>
      r
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
    const parsePrefixes = (r: string) =>
      r
        .replace(/\s+и\s+/gi, ',')
        .split(/[;,\n]+/)
        .map((v) => v.trim())
        .filter(Boolean);
    const next = [
      ...businessCategories,
      {
        id: `cat_${Date.now()}`,
        name,
        keywords: parseTokens(newCategoryKeywords),
        prefixes: parsePrefixes(newCategoryPrefixes),
        is_active: true,
        priority: businessCategories.length + 1,
      },
    ];
    const ok = await saveBizCats(next);
    if (!ok) return;
    setNewCategoryName('');
    setNewCategoryKeywords('');
    setNewCategoryPrefixes('');
  };

  const handleDeleteBizCat = async (id: string) => {
    if (!confirm('Удалить категорию? Привязки у файлов будут сняты.')) return;
    if (selectedBusinessCategory === id) setSelectedBusinessCategory('all');
    await saveBizCats(businessCategories.filter((c) => c.id !== id));
  };

  const handleEditBizCat = async (cat: BusinessCategory) => {
    const nextName = prompt('Название категории', cat.name || '');
    if (nextName === null) return;
    const nextPrefixes = prompt('Префиксы через запятую', (cat.prefixes || []).join(', '));
    if (nextPrefixes === null) return;
    const nextKeywords = prompt('Ключевые слова через запятую', (cat.keywords || []).join(', '));
    if (nextKeywords === null) return;
    const parsePrefixes = (r: string) =>
      r
        .replace(/\s+и\s+/gi, ',')
        .split(/[;,\n]+/)
        .map((v) => v.trim())
        .filter(Boolean);
    const parseTokens = (r: string) =>
      r
        .split(',')
        .map((v) => v.trim())
        .filter(Boolean);
    const next = businessCategories
      .map((c) =>
        c.id !== cat.id
          ? c
          : {
              ...c,
              name: nextName.trim(),
              prefixes: parsePrefixes(nextPrefixes),
              keywords: parseTokens(nextKeywords),
            },
      )
      .filter((c) => c.name);
    await saveBizCats(next);
  };

  const handleSetFileBizCat = async (file: CloudFile, categoryId: string) => {
    const ok = await ds.setCloudFileCategory({
      article: file.article,
      category: file.category,
      filename: file.name,
      categoryId,
    });
    if (!ok) return alert('Не удалось назначить категорию');
    await refresh();
    await loadBizCats();
  };

  /* ─── category visibility ─── */
  const getCatViewKey = (article: string, cat: string) => `${article}:${cat}`;
  const ensureVisible = (article: string, cat: string, total: number) =>
    Math.min(categoryVisibleCount[getCatViewKey(article, cat)] || INITIAL_CATEGORY_ITEMS, total);
  const showMore = (article: string, cat: string, total: number) => {
    const key = getCatViewKey(article, cat);
    setCategoryVisibleCount((prev) => ({
      ...prev,
      [key]: Math.min((prev[key] || INITIAL_CATEGORY_ITEMS) + CATEGORY_ITEMS_STEP, total),
    }));
  };
  useEffect(() => {
    setCategoryVisibleCount({});
  }, [expandedArticle]);

  /* ─── source maps ─── */
  const sourceThumbMap = useMemo(() => {
    const m: Record<string, string> = {};
    files.forEach((f) => {
      if (f.type !== 'upload') return;
      const t = f.thumb || f.url || '';
      if (!t) return;
      m[f.name] = t;
      const b = f.name.split('/').pop()!;
      if (!m[b]) m[b] = t;
    });
    return m;
  }, [files]);
  const sourceUrlMap = useMemo(() => {
    const m: Record<string, string> = {};
    files.forEach((f) => {
      if (f.type !== 'upload' && f.type !== 'linked') return;
      const u = f.url || '';
      if (!u) return;
      m[f.name] = u;
      const b = (f.name || '').split('/').pop()!;
      if (b && !m[b]) m[b] = u;
    });
    return m;
  }, [files]);

  /* ─── article items ─── */
  const items = useMemo<ArticleItem[]>(() => {
    const map: Record<string, ArticleItem> = {};
    const now = Date.now();
    const DAY = 86400000;
    cloudFiles.forEach((f) => {
      if (f.type !== 'cloud' || !f.article) return;
      if (excludeMockups && f.category === 'mockups') return;
      if (selectedBusinessCategory !== 'all' && (f.category_id || '') !== selectedBusinessCategory)
        return;
      if (dateFilter !== 'all') {
        const ft = (f.article_created_mtime || f.mtime) * 1000;
        if (dateFilter === 'today') {
          const s = new Date();
          s.setHours(0, 0, 0, 0);
          if (ft < s.getTime()) return;
        } else if (dateFilter === 'week' && now - ft > 7 * DAY) return;
        else if (dateFilter === 'month' && now - ft > 30 * DAY) return;
      }
      if (filter.trim()) {
        const sl = filter.toLowerCase();
        if (
          ![f.article, f.name, f.print_name, f.product_name, f.render_prefix, f.category_name].some(
            (v) => v && v.toLowerCase().includes(sl),
          )
        )
          return;
      }
      const art = f.article;
      const cat = f.category || 'files';
      const sThumb = f.source_name
        ? sourceThumbMap[f.source_name] || sourceThumbMap[f.source_name.split('/').pop()!]
        : undefined;
      const sUrl =
        f.source_url ||
        (f.source_name
          ? sourceUrlMap[f.source_name] || sourceUrlMap[f.source_name.split('/').pop()!]
          : '');
      if (!map[art])
        map[art] = {
          key: art,
          article: art,
          thumbnail: sThumb || f.article_thumb || f.thumb || f.url,
          mtime: f.article_created_mtime || f.mtime,
          print_name: f.print_name || '',
          source_name: f.source_name || '',
          source_url: sUrl || '',
          categories: {},
        };
      if (sThumb) map[art].thumbnail = sThumb;
      if (!map[art].print_name && f.print_name) map[art].print_name = f.print_name;
      if (!map[art].source_name && f.source_name) map[art].source_name = f.source_name;
      if (!map[art].source_url && sUrl) map[art].source_url = sUrl;
      const at = f.article_created_mtime || f.mtime;
      if (at > map[art].mtime) map[art].mtime = at;
      if (!map[art].categories[cat]) map[art].categories[cat] = [];
      map[art].categories[cat].push(f);
    });
    return Object.values(map).sort((a, b) => {
      // Sort by mtime descending (newest first) like Gallery
      return (b.mtime || 0) - (a.mtime || 0);
    });
  }, [
    cloudFiles,
    filter,
    dateFilter,
    sourceThumbMap,
    sourceUrlMap,
    selectedBusinessCategory,
    excludeMockups,
  ]);

  /* ─── notification helper ─── */
  const notify = (msg: string) => {
    setNotification(msg);
    setTimeout(() => setNotification(null), 2000);
  };
  const copyText = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const t = document.createElement('textarea');
      t.value = text;
      document.body.appendChild(t);
      t.select();
      document.execCommand('copy');
      document.body.removeChild(t);
    }
    notify(`${label} скопировано`);
  };

  /* ─── actions ─── */
  const handleCopyLink = (url: string, file?: CloudFile) => {
    let link = url;
    if (useShortLinks && file?.category === 'products' && file.short_url) link = file.short_url;
    const full = /^https?:\/\//i.test(link) ? link : window.location.origin + link;
    copyText(full, 'Ссылка');
  };

  const handleDownloadArticle = async (item: ArticleItem) => {
    setIsZipping(true);
    try {
      const zip = new JSZip();
      for (const [catName, fileList] of Object.entries(item.categories)) {
        const folder = zip.folder(
          catName === 'products' ? 'Товары' : catName === 'mockups' ? 'Заготовки' : 'Файлы',
        )!;
        await Promise.all(
          fileList.map(async (f) => {
            try {
              const blob = await fetch(f.url).then((r) => r.blob());
              if (blob) folder.file(f.name, blob);
            } catch (e) {
              console.error(e);
            }
          }),
        );
      }
      saveAs(await zip.generateAsync({ type: 'blob' }), `${item.article}.zip`);
    } catch {
      alert('Ошибка создания архива');
    } finally {
      setIsZipping(false);
    }
  };

  const handleDownloadCategory = async (
    articleKey: string,
    catName: string,
    fileList: CloudFile[],
  ) => {
    setIsZipping(true);
    try {
      const zip = new JSZip();
      await Promise.all(
        fileList.map(async (f) => {
          try {
            const blob = await fetch(f.url).then((r) => r.blob());
            if (blob) zip.file(f.name, blob);
          } catch (e) {
            console.error(e);
          }
        }),
      );
      saveAs(
        await zip.generateAsync({ type: 'blob' }),
        `${articleKey}_${catName === 'products' ? 'Товары' : 'Заготовки'}.zip`,
      );
    } catch {
      alert('Ошибка создания архива');
    } finally {
      setIsZipping(false);
    }
  };

  const handleDeleteArticle = async (key: string) => {
    if (!confirm(`Удалить артикул "${key}" со всеми файлами?`)) return;
    setBusy({ type: 'article', key });
    const ok = await ds.deleteCloudArticle({ article: key });
    setBusy({ type: null, key: null });
    if (!ok) return alert('Не удалось удалить');
    await refresh();
  };

  const handleDeleteCategory = async (articleKey: string, category: string) => {
    if (!confirm(`Удалить категорию "${category}" внутри "${articleKey}"?`)) return;
    setBusy({ type: 'category', key: `${articleKey}:${category}` });
    const ok = await ds.deleteCloudCategory({ article: articleKey, category });
    setBusy({ type: null, key: null });
    if (!ok) return alert('Не удалось удалить');
    await refresh();
  };

  const handleDeleteFile = async (file: CloudFile) => {
    if (!confirm(`Удалить файл "${file.name}"?`)) return;
    setBusy({ type: 'file', key: file.url });
    const ok = await ds.deleteCloudFile({
      filename: file.name,
      article: file.article,
      category: file.category,
    });
    setBusy({ type: null, key: null });
    if (!ok) return alert('Не удалось удалить');
    await refresh();
  };

  const handleUploadToCategory = async (
    articleKey: string,
    category: string,
    fileList: FileList,
  ) => {
    if (!fileList.length) return;
    setBusy({ type: 'upload', key: `${articleKey}:${category}` });
    try {
      const fd = new FormData();
      fd.append('type', 'cloud');
      fd.append('article', articleKey);
      fd.append('category', category);
      Array.from(fileList).forEach((f) => fd.append('files', f));
      const res = await apiPostForm<{ success: boolean; message?: string }>(
        '/api/files/upload',
        fd,
      );
      if (!res.success) alert('Ошибка загрузки: ' + (res.message || ''));
      else notify(`Загружено файлов: ${fileList.length}`);
    } catch {
      alert('Ошибка загрузки');
    } finally {
      setBusy({ type: null, key: null });
      await refresh();
    }
  };

  /* ─── print name editing ─── */
  const handleEditPrintName = (item: ArticleItem) => {
    setEditingArticleKey(item.key);
    setEditingPrintNameValue(item.print_name || item.source_name || '');
  };
  const handleSavePrintName = async (item: ArticleItem) => {
    const ok = await ds.updateCloudArticleMeta({
      article: item.article,
      printName: editingPrintNameValue.trim(),
    });
    if (!ok) return alert('Не удалось сохранить');
    setEditingArticleKey(null);
    await refresh();
  };

  const handleToggleShortLinks = (v: boolean) => {
    setUseShortLinks(v);
    localStorage.setItem('cloud_copy_short_links', String(v));
  };

  /* ─── source download ─── */
  const getSourceDownloadUrl = (sn: string) => {
    const raw = sn.trim();
    if (!raw) return '';
    const enc = raw
      .replace(/\\/g, '/')
      .replace(/^\/+/, '')
      .split('/')
      .filter(Boolean)
      .map((p) => encodeURIComponent(p))
      .join('/');
    if (!enc) return '';
    if (enc.startsWith('uploads/')) return `/${enc}`;
    if (enc.startsWith('source/')) return `/uploads/${enc}`;
    return `/uploads/${enc}`;
  };

  const handleDownloadSource = async (sourceName: string, sourceUrl = '') => {
    const localUrl = getSourceDownloadUrl(sourceName);
    const remote = sourceUrl.trim();
    const fallback = sourceName.split('/').pop() || 'source_file';
    const guessed = remote
      ? (() => {
          try {
            return new URL(remote, location.origin).pathname.split('/').pop() || '';
          } catch {
            return '';
          }
        })()
      : '';
    const finalName = guessed || fallback;
    if (!localUrl && !remote) return;
    try {
      const params = new URLSearchParams();
      if (sourceName) params.set('source_name', sourceName);
      if (remote) params.set('source_url', remote);
      params.set('filename', finalName);
      const resp = await fetch(`/api/files/download-source?${params}`, { cache: 'no-store' });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      if (!blob.size) throw new Error('Пустой файл');
      saveAs(blob, finalName);
      notify('Исходник скачан');
    } catch {
      alert('Не удалось скачать исходник');
    }
  };

  /* ─── mass upload ─── */
  const extractArticle = (fn: string) => {
    const b = fn.replace(/\.[^/.]+$/, '');
    let m = b.match(/[-_](\d{5,})/);
    if (m) return m[1];
    m = b.match(/(\d{5,})/);
    return m ? m[1] : null;
  };

  const handleMassSelect = (fileList: FileList) => {
    const arr = Array.from(fileList);
    const known = new Set(
      cloudFiles.filter((f) => f.type === 'cloud' && f.article).map((f) => f.article),
    );
    const matched: MassUploadPreview['matched'] = [];
    const unmatched: MassUploadPreview['unmatched'] = [];
    arr.forEach((file) => {
      const art = extractArticle(file.name);
      if (art && known.has(art)) matched.push({ file, article: art });
      else if (art)
        unmatched.push({
          file,
          article: art,
          status: 'new_article',
          reason: `Артикул ${art} не найден в облаке`,
        });
      else
        unmatched.push({
          file,
          article: null,
          status: 'no_article',
          reason: 'Не удалось определить артикул',
        });
    });
    setMassUploadPreview({ matched, unmatched, allFiles: arr });
    setMassUploadAllowNew(false);
    setMassUploadResult(null);
  };

  const handleMassConfirm = async () => {
    if (!massUploadPreview) return;
    const { matched, unmatched } = massUploadPreview;
    const toUpload: File[] = [...matched.map((m) => m.file)];
    if (massUploadAllowNew)
      unmatched.filter((u) => u.status === 'new_article').forEach((u) => toUpload.push(u.file));
    if (!toUpload.length) {
      alert('Нет подходящих файлов');
      return;
    }
    setMassUploadPreview(null);
    setIsMassUploading(true);
    setMassUploadProgress({ done: 0, total: toUpload.length, currentFile: '' });
    try {
      const data = await ds.massUploadToCloud(toUpload, massUploadCategory, {
        allowNewArticles: massUploadAllowNew,
        onProgress: (done: number, total: number, fileName?: string) =>
          setMassUploadProgress({ done, total, currentFile: fileName || '' }),
      });
      setMassUploadResult({
        uploaded: data.uploaded || 0,
        errors: data.errors || [],
        total: data.total || 0,
        matchedCount: matched.length,
        newArticleCount: massUploadAllowNew
          ? unmatched.filter((u) => u.status === 'new_article').length
          : 0,
        skippedCount: unmatched.filter((u) => u.status === 'no_article').length,
      });
      if ((data.uploaded || 0) > 0) {
        notify(`Загружено: ${data.uploaded} из ${data.total} файлов`);
        await refresh();
      }
    } catch {
      alert('Ошибка массовой загрузки');
    } finally {
      setIsMassUploading(false);
      setMassUploadProgress(null);
    }
  };

  /* click outside category menu */
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('.cloud-file-category-select'))
        setOpenedFileCategoryMenuKey(null);
    };
    document.addEventListener('click', h);
    return () => document.removeEventListener('click', h);
  }, []);

  /* ─── RENDER ─── */
  return (
    <div className="space-y-6 pb-10">
      {/* ── Toolbar ── */}
      <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/60 rounded-2xl shadow-xl p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative group flex-1 min-w-[200px] max-w-xs">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon
                name="search"
                className="w-4 h-4 text-slate-500 group-focus-within:text-blue-400 transition-colors"
              />
            </div>
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Поиск по артикулу..."
              className="w-full bg-slate-800/60 border border-slate-700/50 focus:border-blue-500/50 rounded-xl pl-9 pr-4 py-2 text-sm text-slate-200 outline-none transition-all placeholder-slate-600"
            />
          </div>

          {/* Date filter */}
          <div className="relative border-l border-slate-700/50 pl-3">
            <div className="absolute left-5 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon name="calendar" className="w-4 h-4 text-slate-500" />
            </div>
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as typeof dateFilter)}
              className="bg-transparent text-slate-400 text-sm font-medium pl-8 pr-2 py-1.5 outline-none cursor-pointer hover:text-white transition-colors appearance-none"
            >
              <option value="all" className="bg-slate-900">Все время</option>
              <option value="today" className="bg-slate-900">Сегодня</option>
              <option value="week" className="bg-slate-900">Неделя</option>
              <option value="month" className="bg-slate-900">Месяц</option>
            </select>
          </div>

          <div className="hidden md:block w-px h-8 bg-slate-700/50" />

          {/* Short Links Toggle */}
          <ToggleSwitch
            label="Короткие ссылки"
            icon="link"
            color="blue"
            checked={useShortLinks}
            onChange={handleToggleShortLinks}
          />
          <div className="hidden md:block w-px h-8 bg-slate-700/50" />

          {/* Exclude Mockups */}
          <ToggleSwitch
            label="Исключить заготовки"
            icon="eye-off"
            color="indigo"
            checked={excludeMockups}
            onChange={setExcludeMockups}
          />
          <div className="flex-1" />

          {/* Mass Upload */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-slate-800/50 border border-slate-700/50 rounded-xl p-1">
              {(['products', 'mockups'] as const).map((c) => (
                <button
                  key={c}
                  onClick={() => setMassUploadCategory(c)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${massUploadCategory === c ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25' : 'text-slate-400 hover:text-white hover:bg-slate-700/50'}`}
                >
                  {c === 'products' ? 'Товары' : 'Заготовки'}
                </button>
              ))}
            </div>
            <label
              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all cursor-pointer border shadow-lg ${isMassUploading ? 'bg-slate-700 text-slate-400 border-slate-600 cursor-wait' : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white border-blue-400/30 shadow-blue-500/20 active:scale-[0.98]'}`}
            >
              <Icon
                name={isMassUploading ? 'loader-2' : 'upload'}
                className={`w-4 h-4 ${isMassUploading ? 'animate-spin' : ''}`}
              />
              <span>{isMassUploading ? 'Загрузка...' : 'Массовая загрузка'}</span>
              <input
                type="file"
                multiple
                accept="image/*"
                className="hidden"
                disabled={isMassUploading}
                onChange={(e) => {
                  if (e.target.files?.length) {
                    handleMassSelect(e.target.files);
                    e.target.value = '';
                  }
                }}
              />
            </label>
          </div>
        </div>

        {/* Mass Upload Progress */}
        {massUploadProgress && isMassUploading && (
          <div className="mt-3 p-4 rounded-xl border bg-slate-800/60 border-slate-700/50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Icon name="loader-2" className="w-4 h-4 text-blue-400 animate-spin" />
                <span className="text-sm font-semibold text-white">Загрузка файлов...</span>
              </div>
              <span className="text-sm font-bold text-blue-400">
                {massUploadProgress.done} / {massUploadProgress.total}
              </span>
            </div>
            <div className="w-full h-2 bg-slate-900 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-300"
                style={{
                  width: `${Math.round((massUploadProgress.done / massUploadProgress.total) * 100)}%`,
                }}
              />
            </div>
            {massUploadProgress.currentFile && (
              <p className="text-[11px] text-slate-500 truncate mt-2">
                Текущий: {massUploadProgress.currentFile}
              </p>
            )}
          </div>
        )}

        {/* Mass Upload Results */}
        {massUploadResult && !isMassUploading && (
          <div className="mt-3 p-4 rounded-xl border bg-slate-800/50 border-slate-700/50">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-white">Результат загрузки</span>
              <button
                onClick={() => setMassUploadResult(null)}
                className="p-1 hover:bg-slate-700 rounded-lg text-slate-400 hover:text-white transition-colors"
              >
                <Icon name="x" className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <StatBox color="emerald" label="Загружено" value={massUploadResult.uploaded} />
              <StatBox color="blue" label="Совпало" value={massUploadResult.matchedCount || 0} />
              {(massUploadResult.newArticleCount || 0) > 0 && (
                <StatBox
                  color="amber"
                  label="Новых артикулов"
                  value={massUploadResult.newArticleCount}
                />
              )}
              {massUploadResult.errors?.length > 0 && (
                <StatBox color="red" label="Ошибок" value={massUploadResult.errors.length} />
              )}
            </div>
            {massUploadResult.errors?.length > 0 && (
              <div className="space-y-1 max-h-[150px] overflow-y-auto custom-scrollbar">
                {massUploadResult.errors.map((err: any, i: number) => (
                  <div
                    key={i}
                    className="text-[11px] text-red-300 bg-red-500/10 px-2 py-1 rounded-lg border border-red-500/20"
                  >
                    <span className="font-semibold">{err.file}:</span> {err.error}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Mass Upload Preview Modal ── */}
      {massUploadPreview && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 fade-in"
          onClick={() => setMassUploadPreview(null)}
        >
          <div
            className="bg-slate-900 rounded-2xl border border-slate-700 max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-blue-600 to-indigo-600 w-10 h-10 rounded-xl flex items-center justify-center shadow-lg">
                  <Icon name="upload" className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Предпросмотр загрузки</h3>
                  <p className="text-xs text-slate-400">
                    Проверьте файлы перед загрузкой в «
                    {massUploadCategory === 'products' ? 'Товары' : 'Заготовки'}»
                  </p>
                </div>
              </div>
              <button
                onClick={() => setMassUploadPreview(null)}
                className="p-2 hover:bg-slate-800 rounded-xl text-slate-400 hover:text-white transition-colors"
              >
                <Icon name="x" className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4 custom-scrollbar">
              <div className="grid grid-cols-3 gap-3">
                <StatBox
                  color="slate"
                  label="Всего файлов"
                  value={massUploadPreview.allFiles.length}
                />
                <StatBox
                  color="emerald"
                  label="Совпало с артикулами"
                  value={massUploadPreview.matched.length}
                />
                <StatBox
                  color={massUploadPreview.unmatched.length > 0 ? 'amber' : 'slate'}
                  label="Без совпадений"
                  value={massUploadPreview.unmatched.length}
                />
              </div>
              {massUploadPreview.matched.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                    <Icon name="check-circle" className="w-4 h-4" /> Совпавшие (
                    {massUploadPreview.matched.length})
                  </h4>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto custom-scrollbar">
                    {massUploadPreview.matched.map((m, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 px-3 py-2 bg-emerald-500/5 border border-emerald-500/10 rounded-lg text-xs"
                      >
                        <Icon name="file-image" className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-slate-300 truncate flex-1">{m.file.name}</span>
                        <span className="text-emerald-400 font-mono font-semibold">
                          → {m.article}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {massUploadPreview.unmatched.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
                    <Icon name="alert-triangle" className="w-4 h-4" /> Без совпадений (
                    {massUploadPreview.unmatched.length})
                  </h4>
                  <div className="space-y-1 max-h-[200px] overflow-y-auto custom-scrollbar">
                    {massUploadPreview.unmatched.map((u, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-3 px-3 py-2 bg-amber-500/5 border border-amber-500/10 rounded-lg text-xs"
                      >
                        <Icon
                          name={u.status === 'no_article' ? 'file-x' : 'file-plus'}
                          className="w-3.5 h-3.5 text-amber-400"
                        />
                        <span className="text-slate-300 truncate flex-1">{u.file.name}</span>
                        <span className="text-amber-300/80 text-[11px]">{u.reason}</span>
                      </div>
                    ))}
                  </div>
                  {massUploadPreview.unmatched.some((u) => u.status === 'new_article') && (
                    <label className="mt-3 inline-flex items-center gap-2.5 text-xs text-slate-300 cursor-pointer select-none px-3 py-2.5 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-amber-500/40 transition-colors">
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={massUploadAllowNew}
                        onChange={(e) => setMassUploadAllowNew(e.target.checked)}
                      />
                      <span
                        className={`relative h-5 w-9 rounded-full transition-all ${massUploadAllowNew ? 'bg-amber-500' : 'bg-slate-700'}`}
                      >
                        <span
                          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-md transition-all ${massUploadAllowNew ? 'left-[18px]' : 'left-0.5'}`}
                        />
                      </span>
                      <span className="font-medium">Создать новые артикулы для этих файлов</span>
                    </label>
                  )}
                </div>
              )}
            </div>
            <div className="flex items-center justify-between p-5 border-t border-slate-700 bg-slate-800/30">
              <div className="text-xs text-slate-500">
                {(() => {
                  const c =
                    massUploadPreview.matched.length +
                    (massUploadAllowNew
                      ? massUploadPreview.unmatched.filter((u) => u.status === 'new_article').length
                      : 0);
                  return `Будет загружено: ${c} из ${massUploadPreview.allFiles.length} файлов`;
                })()}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setMassUploadPreview(null)}
                  className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl border border-slate-700 text-sm font-medium transition-all"
                >
                  Отмена
                </button>
                <button
                  onClick={handleMassConfirm}
                  disabled={
                    massUploadPreview.matched.length === 0 &&
                    !(
                      massUploadAllowNew &&
                      massUploadPreview.unmatched.some((u) => u.status === 'new_article')
                    )
                  }
                  className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl text-sm font-semibold shadow-lg disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Icon name="upload" className="w-4 h-4" /> Загрузить
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Business Categories Filter ── */}
      <div className="bg-slate-900/80 backdrop-blur-xl border border-slate-700/60 rounded-2xl shadow-xl p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="bg-gradient-to-br from-indigo-600 to-purple-600 w-8 h-8 rounded-lg flex items-center justify-center shadow-lg">
            <Icon name="tag" className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-sm font-semibold text-white">Категории товаров</div>
            <div className="text-xs text-slate-400">Фильтр по имени мокапа и префиксу</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setSelectedBusinessCategory('all')}
            className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all ${selectedBusinessCategory === 'all' ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:bg-slate-700/50 hover:text-white'}`}
          >
            Все категории
          </button>
          {businessCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedBusinessCategory(cat.id)}
              className={`px-3 py-1.5 rounded-xl text-sm font-medium transition-all ${selectedBusinessCategory === cat.id ? 'bg-blue-600 text-white shadow-lg' : 'bg-slate-800/50 text-slate-300 border border-slate-700/50 hover:bg-slate-700/50 hover:text-white'}`}
              title={`Файлов: ${categoryStats[cat.id] || 0}`}
            >
              {cat.name} ({categoryStats[cat.id] || 0})
            </button>
          ))}
        </div>
      </div>

      {/* ── Article List ── */}
      <div className="space-y-3">
        {isCloudLoading && cloudFiles.length === 0 && (
          <div className="text-center py-20 border border-slate-700/50 rounded-2xl bg-slate-900/50">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600/20 border border-blue-500/20 mb-4">
              <Icon name="loader-2" className="w-8 h-8 text-blue-400 animate-spin" />
            </div>
            <p className="text-slate-300 font-medium text-sm">Загрузка облачных файлов...</p>
          </div>
        )}
        {!isCloudLoading && items.length === 0 && (
          <div className="text-center py-20 text-slate-500 border border-slate-700 rounded-lg bg-slate-900/30">
            <Icon name="folder-open" className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>Нет файлов по текущему фильтру.</p>
          </div>
        )}

        {items.map((item) => {
          const totalFiles = Object.values(item.categories).flat().length;
          const catCount = Object.keys(item.categories).length;
          const isExpanded = expandedArticle === item.key;

          return (
            <div
              key={item.key}
              className="border border-slate-700 rounded-lg overflow-hidden"
              style={{ contentVisibility: 'auto', containIntrinsicSize: '240px' } as any}
            >
              {/* Article header row */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => setExpandedArticle(isExpanded ? null : item.key)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpandedArticle(isExpanded ? null : item.key); } }}
                className="w-full flex items-center gap-4 p-4 bg-slate-800 hover:bg-slate-750 transition-colors text-left cursor-pointer"
              >
                <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-slate-900 border border-slate-700">
                  <img
                    src={item.thumbnail}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0 pr-2">
                  <div className="flex items-center flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-white truncate text-lg">{item.article}</h3>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyText(item.article, 'Артикул');
                        }}
                        className="p-1.5 bg-slate-700 hover:bg-indigo-600 text-slate-300 hover:text-white rounded-lg transition-all flex-shrink-0 shadow-sm border border-slate-600 hover:border-indigo-500 active:scale-95"
                        title="Скопировать артикул"
                      >
                        <Icon name="copy" className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Print name inline editing */}
                    {editingArticleKey === item.key ? (
                      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="text"
                          value={editingPrintNameValue}
                          onChange={(e) => setEditingPrintNameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSavePrintName(item);
                            if (e.key === 'Escape') setEditingArticleKey(null);
                          }}
                          autoFocus
                          className="w-48 bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm text-white outline-none focus:border-indigo-500"
                          placeholder="Имя принта"
                        />
                        <button
                          onClick={() => handleSavePrintName(item)}
                          className="p-1.5 bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600 hover:text-white rounded border border-indigo-500/30 transition-colors"
                        >
                          <Icon name="check" className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setEditingArticleKey(null)}
                          className="p-1.5 bg-slate-700 text-slate-400 hover:text-white rounded hover:bg-slate-600"
                        >
                          <Icon name="x" className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div
                        className="flex items-center gap-2 group/pn cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditPrintName(item);
                        }}
                      >
                        <span
                          className={`text-sm px-2 py-0.5 rounded border ${item.print_name ? 'text-slate-200 bg-slate-800 border-slate-700' : 'text-slate-500 bg-slate-900/30 border-transparent border-dashed border-slate-700'}`}
                        >
                          {item.print_name || 'Имя принта...'}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyText(item.print_name || item.source_name || '', 'Имя принта');
                          }}
                          className="p-1 text-slate-500 hover:text-indigo-400 opacity-0 group-hover/pn:opacity-100 transition-opacity"
                        >
                          <Icon name="copy" className="w-3 h-3" />
                        </button>
                        <Icon
                          name="pencil"
                          className="w-3 h-3 text-slate-600 opacity-0 group-hover/pn:opacity-100 transition-opacity"
                        />
                      </div>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                    <span>
                      {catCount} категорий · {totalFiles} файлов
                    </span>
                    {item.source_name && (
                      <>
                        <span className="text-slate-600">|</span>
                        <div className="flex items-center gap-1.5 min-w-0">
                          <span
                            className="text-slate-500 font-mono truncate max-w-[200px]"
                            title={item.source_name}
                          >
                            src: {item.source_name.split('/').pop()}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              copyText(item.source_name.split('/').pop() || '', 'Имя src');
                            }}
                            className="p-1 text-slate-500 hover:text-indigo-400 rounded hover:bg-slate-800/70"
                          >
                            <Icon name="copy" className="w-3 h-3" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadSource(item.source_name, item.source_url);
                            }}
                            className="p-1 text-slate-500 hover:text-indigo-400 rounded hover:bg-slate-800/70"
                          >
                            <Icon name="download" className="w-3 h-3" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 items-center flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadArticle(item);
                    }}
                    disabled={isZipping}
                    className="h-10 min-w-[132px] flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl transition-all disabled:opacity-50 font-semibold border border-indigo-400/30 shadow-lg whitespace-nowrap"
                  >
                    <Icon
                      name={isZipping ? 'loader-2' : 'download'}
                      className={`w-4 h-4 ${isZipping ? 'animate-spin' : ''}`}
                    />{' '}
                    Весь артикул
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteArticle(item.key);
                    }}
                    disabled={busy.type === 'article' && busy.key === item.key}
                    className="h-10 min-w-[112px] flex items-center justify-center gap-2 px-4 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-200 text-sm rounded-xl border border-red-500/30 transition-all disabled:opacity-50 font-semibold whitespace-nowrap"
                  >
                    <Icon
                      name={
                        busy.type === 'article' && busy.key === item.key ? 'loader-2' : 'trash-2'
                      }
                      className={`w-4 h-4 ${busy.type === 'article' && busy.key === item.key ? 'animate-spin' : ''}`}
                    />{' '}
                    Удалить
                  </button>
                </div>
                <Icon
                  name={isExpanded ? 'chevron-up' : 'chevron-down'}
                  className="w-5 h-5 text-slate-400 flex-shrink-0"
                />
              </div>

              {/* Expanded categories */}
              {isExpanded && (
                <div className="bg-slate-900/30 p-4 border-t border-slate-700 space-y-4">
                  {Object.entries(item.categories).map(([catName, fileList]) => {
                    const catLabel =
                      catName === 'products'
                        ? 'Товары'
                        : catName === 'mockups'
                          ? 'Заготовки'
                          : 'Файлы';
                    const visCount = ensureVisible(item.article, catName, fileList.length);
                    const visible = fileList.slice(0, visCount);
                    const hasMoreCat = fileList.length > visCount;
                    const dragKey = `${item.article}:${catName}`;
                    return (
                      <div
                        key={catName}
                        className={`bg-slate-800/50 rounded-lg p-3 border transition-colors ${dragUploadTarget === dragKey ? 'border-indigo-500 bg-indigo-500/10' : 'border-slate-700/50'}`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          if (busy.type !== 'upload' || busy.key !== dragKey)
                            setDragUploadTarget(dragKey);
                        }}
                        onDragLeave={(e) => {
                          e.preventDefault();
                          setDragUploadTarget((prev) => (prev === dragKey ? null : prev));
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragUploadTarget(null);
                          if (busy.type === 'upload' && busy.key === dragKey) return;
                          const d = e.dataTransfer?.files;
                          if (d?.length) handleUploadToCategory(item.article, catName, d);
                        }}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Icon
                              name={catName === 'products' ? 'package' : 'image'}
                              className="w-4 h-4 text-indigo-400"
                            />
                            <span className="font-semibold text-white/90">{catLabel}</span>
                            <span className="text-xs text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">
                              {fileList.length} шт.
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <label className="flex items-center gap-2 px-3.5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm rounded-xl cursor-pointer border border-indigo-400/30 font-semibold">
                              <Icon name="upload" className="w-4 h-4" /> Загрузить файлы
                              <input
                                type="file"
                                multiple
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  if (e.target.files?.length) {
                                    handleUploadToCategory(item.article, catName, e.target.files);
                                    e.target.value = '';
                                  }
                                }}
                                disabled={busy.type === 'upload' && busy.key === dragKey}
                              />
                            </label>
                            <button
                              onClick={() =>
                                handleDownloadCategory(item.article, catName, fileList)
                              }
                              disabled={isZipping}
                              className="flex items-center gap-2 px-3.5 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm rounded-xl disabled:opacity-50 border border-slate-600 font-semibold"
                            >
                              <Icon name="download" className="w-4 h-4" /> Скачать
                            </button>
                            <button
                              onClick={() => handleDeleteCategory(item.article, catName)}
                              disabled={busy.type === 'category' && busy.key === dragKey}
                              className="flex items-center gap-2 px-3.5 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-300 text-sm rounded-xl border border-red-500/25 disabled:opacity-50 font-semibold"
                            >
                              <Icon name="trash-2" className="w-4 h-4" /> Удалить
                            </button>
                          </div>
                        </div>
                        <div
                          className={`mb-3 text-[11px] px-2.5 py-1.5 rounded border border-dashed ${dragUploadTarget === dragKey ? 'text-indigo-300 border-indigo-400/70 bg-indigo-500/10' : 'text-slate-500 border-slate-700/70 bg-slate-900/30'}`}
                        >
                          Перетащите сюда файлы или нажмите «Загрузить файлы»
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                          {visible.map((f) => (
                            <CloudFileCard
                              key={`${item.article}:${catName}:${f.name}`}
                              file={f}
                              article={item}
                              catName={catName}
                              busy={busy}
                              isZipping={isZipping}
                              useShortLinks={useShortLinks}
                              businessCategories={businessCategories}
                              openedFileCategoryMenuKey={openedFileCategoryMenuKey}
                              onPreview={() => setPreviewFile(f)}
                              onDelete={() => handleDeleteFile(f)}
                              onCopyLink={() => handleCopyLink(f.url, f)}
                              onSetCategory={(cId) => handleSetFileBizCat(f, cId)}
                              onToggleCategoryMenu={(key) =>
                                setOpenedFileCategoryMenuKey((prev) => (prev === key ? null : key))
                              }
                            />
                          ))}
                        </div>
                        {hasMoreCat && (
                          <div className="mt-3 flex justify-center">
                            <button
                              onClick={() => showMore(item.article, catName, fileList.length)}
                              className="px-3.5 py-2 bg-slate-700 hover:bg-slate-600 text-slate-100 text-sm rounded-xl border border-slate-600 font-semibold"
                            >
                              Показать ещё ({fileList.length - visCount})
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Load more */}
      {hasMoreCloud && (
        <div className="flex flex-col items-center gap-2 pt-4">
          <button
            onClick={loadMoreCloud}
            disabled={isCloudLoadingMore}
            className="flex items-center gap-2.5 px-6 py-3 bg-slate-800/80 hover:bg-slate-700/80 text-slate-200 rounded-xl border border-slate-700/60 font-semibold text-sm shadow-lg disabled:opacity-60"
          >
            <Icon
              name={isCloudLoadingMore ? 'loader-2' : 'chevrons-down'}
              className={`w-4 h-4 ${isCloudLoadingMore ? 'animate-spin' : ''}`}
            />{' '}
            {isCloudLoadingMore ? 'Загрузка...' : 'Показать ещё артикулы'}
          </button>
          <span className="text-[11px] text-slate-500">
            Показано {items.length} из ~{totalCloudArticles} артикулов
          </span>
        </div>
      )}
      {!hasMoreCloud && items.length > 0 && !isCloudLoading && (
        <div className="flex justify-center pt-2">
          <span className="text-[11px] text-slate-600 bg-slate-900/30 border border-slate-800 rounded-lg px-3 py-1.5">
            Все артикулы загружены ({items.length})
          </span>
        </div>
      )}

      {/* Preview Modal */}
      {previewFile && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 fade-in"
          onClick={() => {
            setPreviewFile(null);
            setPreviewZoom(1);
          }}
        >
          <div
            className="bg-slate-900 rounded-lg border border-slate-700 max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <h3 className="font-semibold text-white truncate">{previewFile.name}</h3>
              <button
                onClick={() => {
                  setPreviewFile(null);
                  setPreviewZoom(1);
                }}
                className="p-1 hover:bg-slate-800 rounded text-slate-400 hover:text-white"
              >
                <Icon name="x" className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-auto flex items-center justify-center p-4 bg-slate-950">
              <img
                src={previewFile.url}
                alt={previewFile.name}
                style={{ transform: `scale(${previewZoom})`, transition: 'transform 0.2s' }}
                className="max-w-full max-h-full object-contain cursor-zoom-in"
              />
            </div>
            <div className="p-4 border-t border-slate-700 flex items-center justify-between gap-3 bg-slate-800">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPreviewZoom(Math.max(0.5, previewZoom - 0.2))}
                  className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded text-slate-300 hover:text-white"
                >
                  <Icon name="zoom-out" className="w-4 h-4" />
                </button>
                <span className="text-xs text-slate-400 w-12 text-center">
                  {Math.round(previewZoom * 100)}%
                </span>
                <button
                  onClick={() => setPreviewZoom(Math.min(3, previewZoom + 0.2))}
                  className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded text-slate-300 hover:text-white"
                >
                  <Icon name="zoom-in" className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setPreviewZoom(1)}
                  className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded text-slate-300 hover:text-white text-xs"
                >
                  100%
                </button>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleCopyLink(previewFile.url, previewFile)}
                  className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded text-white text-xs flex items-center gap-1.5"
                >
                  <Icon name="copy" className="w-3 h-3" /> Получить ссылку
                </button>
                <button
                  onClick={() => window.open(previewFile.url, '_blank')}
                  className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded text-white text-xs flex items-center gap-1.5"
                >
                  <Icon name="external-link" className="w-3 h-3" /> Открыть
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notification */}
      {notification && (
        <div className="fixed bottom-4 right-4 bg-slate-800 border border-indigo-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 fade-in flex items-center gap-2">
          <Icon name="check" className="w-4 h-4 text-indigo-400" />
          <span className="text-sm font-medium">{notification}</span>
        </div>
      )}
    </div>
  );
};

/* ─── sub-components ─── */

const ToggleSwitch: FC<{
  label: string;
  icon: string;
  color: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ label, icon, color, checked, onChange }) => (
  <div className="flex items-center gap-2.5">
    <Icon name={icon} className={`w-4 h-4 text-${color}-400`} />
    <label className="inline-flex items-center gap-2 text-sm text-slate-300 cursor-pointer select-none">
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        className={`relative h-5 w-9 rounded-full transition-all ${checked ? `bg-${color}-500 shadow-[0_0_8px_rgba(59,130,246,0.3)]` : 'bg-slate-700'}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-md transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`}
        />
      </span>
      <span className="font-medium whitespace-nowrap">{label}</span>
    </label>
  </div>
);

const StatBox: FC<{ color: string; label: string; value: number }> = ({ color, label, value }) => (
  <div
    className={`bg-${color}-500/10 border border-${color}-500/20 rounded-xl px-3 py-2.5 text-center`}
  >
    <div className={`text-lg font-bold text-${color}-400`}>{value}</div>
    <div className={`text-[10px] text-${color}-300/70 font-medium uppercase tracking-wide`}>
      {label}
    </div>
  </div>
);

interface CloudFileCardProps {
  file: CloudFile;
  article: ArticleItem;
  catName: string;
  busy: { type: string | null; key: string | null };
  isZipping: boolean;
  useShortLinks: boolean;
  businessCategories: BusinessCategory[];
  openedFileCategoryMenuKey: string | null;
  onPreview: () => void;
  onDelete: () => void;
  onCopyLink: () => void;
  onSetCategory: (id: string) => void;
  onToggleCategoryMenu: (key: string) => void;
}

const CloudFileCard: FC<CloudFileCardProps> = ({
  file: f,
  article: item,
  catName,
  busy,
  businessCategories,
  openedFileCategoryMenuKey,
  onPreview,
  onDelete,
  onCopyLink,
  onSetCategory,
  onToggleCategoryMenu,
}) => {
  const menuKey = `${item.article}:${catName}:${f.name}`;
  const selectedCat = businessCategories.find((c) => c.id === (f.category_id || ''));
  const selectedLabel = selectedCat?.name || f.category_name || 'Без категории';
  const menuOpened = openedFileCategoryMenuKey === menuKey;

  return (
    <div
      className="group relative bg-slate-900 rounded-xl overflow-hidden border border-slate-700 hover:border-indigo-500 transition-all shadow-lg flex flex-col"
      style={{ contentVisibility: 'auto', containIntrinsicSize: '340px' } as any}
    >
      <div className="aspect-square relative bg-slate-800">
        <img
          src={f.thumb || f.url}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform duration-500"
          onClick={onPreview}
        />
        <div className="absolute inset-0 bg-slate-900/80 opacity-0 group-hover:opacity-100 transition-all flex flex-col items-center justify-center p-3 gap-2 backdrop-blur-sm">
          <div className="grid grid-cols-2 gap-2 w-full">
            <button
              onClick={onPreview}
              className="flex items-center justify-center gap-1.5 px-2 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-[10px] font-bold text-white uppercase tracking-wide"
            >
              <Icon name="eye" className="w-3 h-3" /> Смотреть
            </button>
            <button
              onClick={async () => {
                try {
                  const b = await fetch(f.url).then((r) => r.blob());
                  saveAs(b, f.name);
                } catch {
                  alert('Ошибка скачивания');
                }
              }}
              className="flex items-center justify-center gap-1.5 px-2 py-1.5 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-[10px] font-bold text-white uppercase tracking-wide"
            >
              <Icon name="download" className="w-3 h-3" /> Скачать
            </button>
            <button
              onClick={onDelete}
              disabled={busy.type === 'file' && busy.key === f.url}
              className="flex items-center justify-center gap-1.5 px-2 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/20 rounded-lg text-[10px] font-bold col-span-2 disabled:opacity-50 uppercase tracking-wide"
            >
              <Icon
                name={busy.type === 'file' && busy.key === f.url ? 'loader-2' : 'trash-2'}
                className={`w-3 h-3 ${busy.type === 'file' && busy.key === f.url ? 'animate-spin' : ''}`}
              />{' '}
              Удалить
            </button>
          </div>
        </div>
      </div>
      <div className="p-3 bg-slate-800 border-t border-slate-700 flex flex-col gap-1 relative group/footer">
        <div className="flex justify-between items-start gap-2">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold text-slate-200 truncate" title={f.name}>
              {f.name.replace(/\.[^/.]+$/, '')}
            </p>
            {f.product_name && (
              <p
                className="text-[10px] text-indigo-400 truncate font-medium mt-0.5"
                title={f.product_name}
              >
                {f.product_name}
              </p>
            )}
            {f.render_prefix && (
              <p className="text-[10px] text-slate-500 truncate mt-0.5">
                prefix: {f.render_prefix}
              </p>
            )}
            <div className="mt-1.5">
              <div
                className="relative cloud-file-category-select"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => onToggleCategoryMenu(menuKey)}
                  className="w-full h-7 px-2 rounded-lg border border-slate-700 bg-slate-900/95 hover:border-slate-600 text-[10px] text-slate-200 flex items-center justify-between gap-2 transition-colors"
                >
                  <span className="truncate">{selectedLabel}</span>
                  <Icon
                    name={menuOpened ? 'chevron-up' : 'chevron-down'}
                    className="w-3 h-3 text-slate-400 flex-shrink-0"
                  />
                </button>
                {menuOpened && (
                  <div className="absolute left-0 right-0 top-[30px] z-30 rounded-lg border border-slate-700 bg-slate-900 shadow-2xl overflow-hidden">
                    <button
                      type="button"
                      onClick={() => onSetCategory('')}
                      className={`w-full px-2 py-1.5 text-left text-[10px] transition-colors ${!(f.category_id || '') ? 'bg-indigo-600/20 text-indigo-300' : 'text-slate-300 hover:bg-slate-800'}`}
                    >
                      Без категории
                    </button>
                    {businessCategories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => onSetCategory(cat.id)}
                        className={`w-full px-2 py-1.5 text-left text-[10px] transition-colors ${f.category_id === cat.id ? 'bg-indigo-600/20 text-indigo-300' : 'text-slate-300 hover:bg-slate-800'}`}
                      >
                        {cat.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCopyLink();
            }}
            className="p-1.5 bg-slate-700 hover:bg-indigo-600 text-slate-300 hover:text-white rounded-lg transition-all flex-shrink-0 shadow-sm border border-slate-600 hover:border-indigo-500 active:scale-95"
            title="Копировать ссылку"
          >
            <Icon name="copy" className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
